import { useMobileApi } from "@/Hooks/useMobileApi";
import type { BeginPairingMobileDeviceResponse } from "@freightsimple/generated-dashboard-openapi-client/models/BeginPairingMobileDeviceResponse";
import { asOffsetDateTime } from "@freightsimple/time/branded-types";
import { now, offsetDateTimeToMs } from "@freightsimple/time/helpers";
import { useEffect, useRef, useState } from "react";

// Keep the button's spinner on screen for at least this long so a fast
// response still produces visible press feedback (the request is often
// near-instant locally, which would otherwise flicker too briefly to see).
const MINIMUM_LOADING_MS = 400;

// How long the "New code generated" confirmation stays up after a regenerate.
const CONFIRMATION_MS = 1500;

export interface UsePairMobileDeviceResult {
  /** The current pairing nonce + expiry, or undefined before the first load. */
  pairing: BeginPairingMobileDeviceResponse | undefined;
  loading: boolean;
  error: boolean;
  /** Whole seconds left before the nonce expires (0 once expired). */
  secondsRemaining: number;
  isExpired: boolean;
  /**
   * True for a short window after a *subsequent* regenerate (never on the
   * very first load) so the screen can flash a "New code generated" note.
   */
  justRegenerated: boolean;
  /** Fetch a fresh nonce (used on mount and the "Generate new code" button). */
  regenerate: () => void;
}

export function usePairMobileDevice(): UsePairMobileDeviceResult {
  const mobileApi = useMobileApi();

  const [pairing, setPairing] = useState<
    BeginPairingMobileDeviceResponse | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [justRegenerated, setJustRegenerated] = useState(false);
  // Bumped every second so the countdown derived below stays live.
  const [currentTime, setCurrentTime] = useState(() => now());

  // Tracks the previously-seen nonce so we only flash the confirmation on
  // subsequent regenerations, not the initial load.
  const previousNonceRef = useRef<string | undefined>(undefined);

  async function loadNonce() {
    setLoading(true);
    setError(false);
    const startedAt = performance.now();
    try {
      const response = await mobileApi.beginPairingMobileDevice();
      setPairing(response);
      setCurrentTime(now());
    } catch (e) {
      console.error(e);
      setError(true);
    } finally {
      const elapsed = performance.now() - startedAt;
      if (elapsed < MINIMUM_LOADING_MS) {
        await new Promise((resolve) =>
          setTimeout(resolve, MINIMUM_LOADING_MS - elapsed),
        );
      }
      setLoading(false);
    }
  }

  // Fetch a nonce on mount.
  useEffect(() => {
    loadNonce();
  }, []);

  // Keep the clock ticking so the countdown re-renders each second.
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Flash "New code generated" whenever the nonce changes — but skip the
  // first nonce (the initial load isn't a regeneration the user triggered).
  useEffect(() => {
    const nonce = pairing?.nonce;
    if (nonce === undefined) {
      return;
    }
    if (previousNonceRef.current === undefined) {
      previousNonceRef.current = nonce;
      return;
    }
    if (nonce === previousNonceRef.current) {
      return;
    }
    previousNonceRef.current = nonce;
    setJustRegenerated(true);
    const timeout = setTimeout(
      () => setJustRegenerated(false),
      CONFIRMATION_MS,
    );
    return () => clearTimeout(timeout);
  }, [pairing?.nonce]);

  const secondsRemaining =
    pairing === undefined
      ? 0
      : Math.max(
          0,
          Math.round(
            (offsetDateTimeToMs(asOffsetDateTime(pairing.expiresAt)) -
              offsetDateTimeToMs(currentTime)) /
              1000,
          ),
        );

  const isExpired = pairing !== undefined && secondsRemaining <= 0;

  return {
    pairing,
    loading,
    error,
    secondsRemaining,
    isExpired,
    justRegenerated,
    regenerate: loadNonce,
  };
}
